import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import './ShiftTimePicker.css';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

class ShiftTimePicker extends Component {
    constructor(props) {
        super(props);
        const sel = props.selected ? new Date(props.selected) : null;
        this.state = {
            open: false,
            isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
            pickedHour: sel ? sel.getHours() : 9,
            pickedMinute: sel ? this._snapMinute(sel.getMinutes()) : 0,
        };
        this.hourRef = React.createRef();
        this.minuteRef = React.createRef();
        this.inputRef = React.createRef();
    }

    _snapMinute(m) {
        return Math.round(m / 5) * 5 % 60;
    }

    componentDidMount() {
        this._resizeHandler = () => {
            this.setState({ isMobile: window.innerWidth < 768 });
        };
        window.addEventListener('resize', this._resizeHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._resizeHandler);
    }

    componentDidUpdate(_, prevState) {
        if (!prevState.open && this.state.open) {
            requestAnimationFrame(() => this.scrollToSelected());
        }
    }

    scrollToSelected = () => {
        [this.hourRef, this.minuteRef].forEach(ref => {
            if (!ref.current) return;
            const sel = ref.current.querySelector('.stp-slot--selected');
            if (sel) {
                sel.scrollIntoView({ block: 'center' });
            }
        });
    }

    getSelectedLabel() {
        const { selected } = this.props;
        if (!selected) return '';
        const d = new Date(selected);
        return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    }

    open = () => {
        const sel = this.props.selected ? new Date(this.props.selected) : null;
        this.setState({
            open: true,
            pickedHour: sel ? sel.getHours() : 9,
            pickedMinute: sel ? this._snapMinute(sel.getMinutes()) : 0,
        });
    }

    close = () => {
        const now = new Date();
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), this.state.pickedHour, this.state.pickedMinute, 0, 0);
        this.setState({ open: false });
        if (this.props.onChange) this.props.onChange(date);
    }

    getDesktopPosition() {
        if (!this.inputRef.current) return {};
        const rect = this.inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropUp = spaceBelow < 320;
        const dropdownWidth = Math.max(rect.width, 220);
        const pad = 12;
        let left = rect.left;
        if (left + dropdownWidth > window.innerWidth - pad) {
            left = window.innerWidth - dropdownWidth - pad;
        }
        if (left < pad) left = pad;
        return {
            position: 'fixed',
            left,
            width: dropdownWidth,
            ...(dropUp
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }
            ),
        };
    }

    renderColumns() {
        const { pickedHour, pickedMinute } = this.state;
        return (
            <div className="stp-columns">
                <div className="stp-column" ref={this.hourRef}>
                    <div className="stp-column-label">Hour</div>
                    <div className="stp-column-scroll">
                        {HOURS.map(h => (
                            <button
                                key={h}
                                type="button"
                                className={'stp-slot' + (h === pickedHour ? ' stp-slot--selected' : '')}
                                onClick={() => this.setState({ pickedHour: h })}
                            >
                                {String(h).padStart(2, '0')}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="stp-column-divider" />
                <div className="stp-column" ref={this.minuteRef}>
                    <div className="stp-column-label">Min</div>
                    <div className="stp-column-scroll">
                        {MINUTES.map(m => (
                            <button
                                key={m}
                                type="button"
                                className={'stp-slot' + (m === pickedMinute ? ' stp-slot--selected' : '')}
                                onClick={() => this.setState({ pickedMinute: m })}
                            >
                                {String(m).padStart(2, '0')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderDesktop() {
        if (!this.state.open) return null;
        return ReactDOM.createPortal(
            <React.Fragment>
                <div className="stp-desktop-backdrop" onClick={this.close} />
                <div className="stp-desktop-dropdown" style={this.getDesktopPosition()}>
                    {this.renderColumns()}
                </div>
            </React.Fragment>,
            document.body
        );
    }

    renderMobile() {
        if (!this.state.open) return null;
        return ReactDOM.createPortal(
            <div className="stp-mobile-overlay" onClick={this.close}>
                <div className="stp-mobile-sheet" onClick={e => e.stopPropagation()}>
                    <div className="stp-sheet-header">
                        <span className="stp-sheet-title">{this.props.label || 'Select Time'}</span>
                        <button type="button" className="stp-sheet-close" onClick={this.close}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div className="stp-sheet-divider" />
                    {this.renderColumns()}
                    <div className="stp-confirm-bar">
                        <button type="button" className="stp-confirm-btn" onClick={this.close}>
                            Confirm
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    render() {
        // While open the field shows what's picked so far (it's saved on close)
        const { open, pickedHour, pickedMinute } = this.state;
        const selectedLabel = open
            ? String(pickedHour).padStart(2, '0') + ':' + String(pickedMinute).padStart(2, '0')
            : this.getSelectedLabel();
        return (
            <React.Fragment>
                <div
                    ref={this.inputRef}
                    className="stp-input"
                    onClick={this.open}
                >
                    <span className={selectedLabel ? 'stp-input-value' : 'stp-input-placeholder'}>
                        {selectedLabel || this.props.placeholder || '00:00'}
                    </span>
                </div>
                {this.state.isMobile ? this.renderMobile() : this.renderDesktop()}
            </React.Fragment>
        );
    }
}

export default ShiftTimePicker;
